import { useEffect, useState } from 'react';
import { Button, SelectField } from '@7shifts/sous-chef-base';
import type { SelectOption } from '@7shifts/sous-chef-base';
import { useTerminalAccount } from '@src/context/AccountContext';
import { useTranslation } from '@src/i18n';
import './SelectionPage.css';

export function SelectionPage() {
    const {
        isLoading,
        initParams,
        needsCompanyPick,
        needsLocationPick,
        availableCompanies,
        isLoadingCompanies,
        availableLocations,
        isLoadingLocations,
        selectCompany,
        selectLocation
    } = useTerminalAccount();
    const { t } = useTranslation();

    const [selectedLocationId, setSelectedLocationId] = useState<number | null>(
        null
    );

    // Clear the location selection whenever the company changes.
    useEffect(() => {
        setSelectedLocationId(null);
    }, [initParams]);

    const companyOptions: SelectOption<number>[] = availableCompanies.map(
        c => ({ value: c.id, label: c.name })
    );

    const locationOptions: SelectOption<number>[] = availableLocations.map(
        loc => ({ value: loc.id, label: loc.name })
    );

    const selectedCompanyOption = companyOptions.find(
        o => o.value === initParams?.companyId
    );

    const selectedLocationOption = locationOptions.find(
        o => o.value === selectedLocationId
    );

    const canProceed = initParams !== null && selectedLocationId !== null;

    function handleCompanyChange(option: SelectOption<number>) {
        setSelectedLocationId(null);
        selectCompany(option.value);
    }

    function handleNext() {
        if (initParams !== null && selectedLocationId !== null) {
            selectLocation(selectedLocationId);
        }
    }

    return (
        <main className="selection-page">
            <div className="selection-page__container">
                <h1 className="selection-page__title">
                    {t('selection.title')}
                </h1>
                <div className="selection-page__fields">
                    <SelectField
                        name="company"
                        label={t('selection.companyLabel')}
                        placeholder={
                            isLoadingCompanies
                                ? t('common.loading')
                                : t('selection.companyPlaceholder')
                        }
                        options={companyOptions}
                        value={selectedCompanyOption}
                        onChange={handleCompanyChange}
                        disabled={!needsCompanyPick || isLoadingCompanies}
                        isSearchable={false}
                    />
                    <SelectField
                        name="location"
                        label={t('selection.locationLabel')}
                        placeholder={
                            isLoadingLocations
                                ? t('common.loading')
                                : t('selection.locationPlaceholder')
                        }
                        options={locationOptions}
                        value={selectedLocationOption}
                        onChange={option => setSelectedLocationId(option.value)}
                        disabled={
                            !needsLocationPick ||
                            isLoadingLocations ||
                            !initParams
                        }
                        isSearchable={false}
                    />
                </div>
                <Button
                    theme="primary"
                    size="full-width"
                    type="button"
                    onClick={handleNext}
                    disabled={
                        !canProceed ||
                        isLoading ||
                        isLoadingCompanies ||
                        isLoadingLocations
                    }
                >
                    {isLoading ? t('common.loading') : t('selection.next')}
                </Button>
            </div>
        </main>
    );
}
